import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AccessTime,
  ArrowBack,
  Download,
  OpenInNew,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  ProfilePaper,
  SectionTitleSmall,
} from '~/modules/connect/ExpertProfilePage';
import { LabelValue } from '~/shared/components/LabelValue.tsx';
import { AvatarImage } from '~/shared/components/AvatarImage';
import { RAILS_EDIT_EXPERT_PROFILE_URL } from '~/shared/config';
import { hasValue } from '~/shared/utils/hasValue';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import type {
  CertificateLicenseRow,
  ProfessionalExperienceRow,
  PublicationRow,
  User,
} from '~/shared/types/connect';

interface Props {
  expert: User;
  isOwnProfile: boolean;
  currentUser: User | null;
  mattersWithoutExpert: MatterViewModel[];
  lastActive: string | null;
  setIsCreateMatterModalOpen: (open: boolean) => void;
  onOpenAddToMatterModal: () => void;
  onBack: () => void;
}

function hasExpertMetadataFields(expert: User): boolean {
  return (
    hasValue(expert.industry) ||
    hasValue(expert.statesOfPractice) ||
    hasValue(expert.yearsExperience) ||
    hasValue(expert.subspecialty) ||
    hasValue(expert.previouslyTestified)
  );
}

function getValidCertifications(expert: User): string[] {
  return (expert.certifications ?? []).filter(hasValue);
}

export function ExpertProfileContent({
  expert,
  isOwnProfile,
  currentUser,
  mattersWithoutExpert,
  lastActive,
  setIsCreateMatterModalOpen,
  onOpenAddToMatterModal,
  onBack,
}: Props) {
  const validCertifications = getValidCertifications(expert);
  const hasFields = hasExpertMetadataFields(expert);
  const hasMetadata = hasFields || validCertifications.length > 0;
  return (
    <>
      {!isOwnProfile && (
        <BackButton type="button" onClick={onBack}>
          <ArrowBackIcon aria-hidden />
          Back
        </BackButton>
      )}

      <ProfilePaper variant="outlined">
        <ProfileRow>
          <LastActiveCell>
            {lastActive && (
              <LastActiveWrap>
                <AccessTimeIcon color="action" aria-hidden />
                <Typography variant="caption" color="text.secondary">
                  Last active {format(new Date(lastActive), 'MMM d, yyyy')}
                </Typography>
              </LastActiveWrap>
            )}
          </LastActiveCell>

          <ProfileMain>
            <ProfileAvatar
              avatarUrl={expert.avatarUrl}
              initialsLabel={expert.name ?? ''}
              alt={expert.name}
            />
            <ProfileContentCol>
              <NameBlock>
                <Typography variant="h5" fontWeight={700}>
                  {expert.name}
                </Typography>
                {(expert.credentials ?? expert.specialty) && (
                  <CredentialsMt variant="body2" color="text.secondary">
                    {expert.credentials ?? expert.specialty}
                  </CredentialsMt>
                )}
                <ActionsRow>
                  {!isOwnProfile && currentUser?.role === 'Attorney' && (
                    <>
                      <NewMatterButton
                        variant="contained"
                        size="small"
                        onClick={() => setIsCreateMatterModalOpen(true)}
                        aria-label={`Create new matter with ${expert.name}`}
                      >
                        New Matter
                      </NewMatterButton>
                      {mattersWithoutExpert.length > 0 && (
                        <ProfileButton
                          variant="outlined"
                          size="small"
                          onClick={onOpenAddToMatterModal}
                          aria-label={`Add ${expert.name} to existing matter`}
                        >
                          Add to Matter
                        </ProfileButton>
                      )}
                      <ProfileButton
                        component={Link}
                        to={`/matters?expert=${expert.id}`}
                        variant="outlined"
                        size="small"
                      >
                        View Joint Matters
                      </ProfileButton>
                    </>
                  )}
                  {expert.cvUrl !== null &&
                    expert.cvUrl !== undefined &&
                    expert.cvUrl !== '' && (
                      <CvButton
                        component="a"
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon aria-hidden />}
                        href={expert.cvUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        Download CV
                      </CvButton>
                    )}
                  {isOwnProfile && (
                    <EditProfileButton
                      component="a"
                      href={RAILS_EDIT_EXPERT_PROFILE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      size="small"
                      endIcon={<OpenInNewSmall aria-hidden />}
                      aria-label="Edit profile (opens in new tab)"
                    >
                      Edit Profile
                    </EditProfileButton>
                  )}
                </ActionsRow>
              </NameBlock>
              <LinksRow>
                {expert.linkedInUrl && (
                  <LinkedInLink
                    component="a"
                    href={expert.linkedInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit LinkedIn Profile
                    <OpenInNewSmall aria-hidden />
                  </LinkedInLink>
                )}
              </LinksRow>
            </ProfileContentCol>
          </ProfileMain>
        </ProfileRow>
      </ProfilePaper>

      {(hasValue(expert.professionalSummary) || hasMetadata) && (
        <ProfilePaper variant="outlined">
          <SectionTitleSmall variant="subtitle1" fontWeight={700}>
            Professional summary
          </SectionTitleSmall>
          {hasValue(expert.professionalSummary) && (
            <SummaryText variant="body2" color="text.secondary">
              {expert.professionalSummary}
            </SummaryText>
          )}
          {hasFields && (
            <GridBlock>
              {hasValue(expert.industry) && (
                <LabelValue label="Industry" value={expert.industry} />
              )}
              {hasValue(expert.statesOfPractice) && (
                <LabelValue
                  label="State(s) of Practice"
                  value={expert.statesOfPractice}
                />
              )}
              {hasValue(expert.yearsExperience) && (
                <LabelValue
                  label="Years Experience"
                  value={expert.yearsExperience}
                />
              )}
              {hasValue(expert.subspecialty) && (
                <LabelValue label="Subspecialty" value={expert.subspecialty} />
              )}
              {hasValue(expert.previouslyTestified) && (
                <LabelValue
                  label="Previously Testified"
                  value={expert.previouslyTestified ? 'Yes' : 'No'}
                />
              )}
            </GridBlock>
          )}
          {validCertifications.length > 0 && (
            <CertificationsWrap>
              <CertificationsLabel variant="body2" color="text.secondary">
                Certifications
              </CertificationsLabel>
              <ChipsWrap>
                {validCertifications.map((cert, index) => (
                  <CertificationChip
                    key={`${cert}_${index}`}
                    label={cert}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </ChipsWrap>
            </CertificationsWrap>
          )}
        </ProfilePaper>
      )}

      {expert.certificatesLicenses &&
        expert.certificatesLicenses.length > 0 && (
          <ProfilePaper variant="outlined">
            <SectionTitleSmall variant="subtitle1" fontWeight={700}>
              Certificates and Licences
            </SectionTitleSmall>
            <TableScroll>
              <SmallTable size="small" aria-label="Certificates and licences">
                <TableHead>
                  <TableRow>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Issuing body</TableHeadCell>
                    <TableHeadCell>Date</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expert.certificatesLicenses.map(
                    (row: CertificateLicenseRow) => (
                      <TableRow
                        key={`${row.name}-${row.issuingBody}-${row.date}`}
                      >
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.issuingBody}</TableCell>
                        <TableCell>{row.date}</TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </SmallTable>
            </TableScroll>
          </ProfilePaper>
        )}

      {expert.professionalExperience &&
        expert.professionalExperience.length > 0 && (
          <ProfilePaper variant="outlined">
            <SectionTitleSmall variant="subtitle1" fontWeight={700}>
              Professional experience
            </SectionTitleSmall>
            <ContentList>
              {expert.professionalExperience.map(
                (
                  professionalExperience: ProfessionalExperienceRow,
                  index: number,
                ) => (
                  <ContentItem
                    key={`${professionalExperience.role}-${professionalExperience.organization}-${professionalExperience.period}`}
                    isLast={
                      index === (expert.professionalExperience?.length ?? 0) - 1
                    }
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {professionalExperience.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {professionalExperience.organization} ·{' '}
                      {professionalExperience.period}
                    </Typography>
                    {professionalExperience.description && (
                      <DescriptionMt variant="body2" color="text.secondary">
                        {professionalExperience.description}
                      </DescriptionMt>
                    )}
                  </ContentItem>
                ),
              )}
            </ContentList>
          </ProfilePaper>
        )}

      {expert.publications && expert.publications.length > 0 && (
        <ProfilePaper variant="outlined">
          <SectionTitleSmall variant="subtitle1" fontWeight={700}>
            Publications & Peer Reviewed Work
          </SectionTitleSmall>
          <ContentList>
            {expert.publications.map(
              (publication: PublicationRow, index: number) => (
                <ContentItem
                  key={`${publication.title}-${publication.journal}-${
                    publication.year ?? ''
                  }`}
                  isLast={index === (expert.publications?.length ?? 0) - 1}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {publication.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {publication.journal}
                    {publication.year && ` · ${publication.year}`}
                  </Typography>
                  {publication.authors && (
                    <AuthorsMt variant="body2" color="text.secondary">
                      {publication.authors}
                    </AuthorsMt>
                  )}
                </ContentItem>
              ),
            )}
          </ContentList>
        </ProfilePaper>
      )}
    </>
  );
}

const BackButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
  textDecoration: 'underline',
  ...theme.typography.body2,
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  '&:hover': { color: theme.palette.text.primary },
}));

const ArrowBackIcon = styled(ArrowBack)(() => ({
  fontSize: 18,
}));

const ProfileRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(3),
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
}));

const LastActiveCell = styled(Stack)(({ theme }) => ({
  order: -1,
  flexShrink: 0,
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'flex-start',
  [theme.breakpoints.up('md')]: {
    order: 1,
    width: 'auto',
    justifyContent: 'flex-end',
  },
}));

const LastActiveWrap = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const AccessTimeIcon = styled(AccessTime)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
}));

const ProfileMain = styled(Stack)(({ theme }) => ({
  order: 0,
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
  gap: theme.spacing(2),
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 'auto',
  },
}));

const ProfileAvatar = styled(AvatarImage)(() => ({
  width: 96,
  height: 96,
  flexShrink: 0,
}));

const ProfileContentCol = styled(Stack)(({ theme }) => ({
  minWidth: 0,
  flex: 1,
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    textAlign: 'left',
    width: 'auto',
  },
}));

const NameBlock = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    textAlign: 'left',
    width: 'auto',
  },
}));

const ActionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  flexWrap: 'nowrap',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    width: 'auto',
  },
}));

const ProfileButton = styled(Button)(({ theme }) => ({
  minHeight: 32,
  flex: '0 0 auto',
  fontSize: theme.typography.body2.fontSize,
  width: '100%',
  [theme.breakpoints.up('md')]: {
    width: 'fit-content',
  },
})) as typeof Button;

const NewMatterButton = styled(Button)(({ theme }) => ({
  minHeight: 32,
  minWidth: 120,
  flex: '0 0 auto',
  fontSize: '0.8125rem',
  whiteSpace: 'nowrap',
  width: '100%',
  [theme.breakpoints.up('md')]: { width: 'fit-content' },
  backgroundColor: theme.palette.darkNavy,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.amount.main,
  },
  '& .MuiButton-endIcon': { color: theme.palette.common.white },
})) as typeof Button;

const EditProfileButton = styled(Button)(({ theme }) => ({
  minHeight: 32,
  fontSize: '0.8125rem',
  width: '100%',
  maxWidth: '100%',
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  textDecoration: 'none',
  color: theme.palette.common.white,
  [theme.breakpoints.up('md')]: { width: 'fit-content' },
})) as typeof Button;

const CredentialsMt = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
}));

const LinksRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),
}));

const LinkedInLink = styled(Stack)(({ theme }) => ({
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  ...theme.typography.body2,
  '&:hover': { color: theme.palette.primary.dark },
})) as typeof Stack;

const GridBlock = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const CertificationsWrap = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const CertificationsLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const ChipsWrap = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

const CertificationChip = styled(Chip)(({ theme }) => ({
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  fontWeight: 600,
  '& .MuiChip-label': { fontSize: '0.8125rem' },
}));

const CvButton = styled(Button)(({ theme }) => ({
  minHeight: 32,
  flex: '0 0 auto',
  fontSize: '0.8125rem',
  textDecoration: 'none',
  width: '100%',
  [theme.breakpoints.up('md')]: { width: 'fit-content' },
})) as typeof Button;

const DownloadIcon = styled(Download)(() => ({ fontSize: 16 }));
const OpenInNewSmall = styled(OpenInNew)(() => ({ fontSize: 14 }));

const SummaryText = styled(Typography)(() => ({
  whiteSpace: 'pre-line',
}));

const TableScroll = styled(TableContainer)(() => ({
  overflowX: 'auto',
}));

const SmallTable = styled(Table)(() => ({
  minWidth: 400,
}));

const TableHeadCell = styled(TableCell)(() => ({
  fontWeight: 600,
}));

const ContentList = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ContentItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<{ isLast: boolean }>(({ theme, isLast }) => ({
  paddingBottom: isLast ? 0 : theme.spacing(2),
  borderBottom: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
}));

const DescriptionMt = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const AuthorsMt = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
}));
